import clsx from 'clsx';
import { forwardRef } from 'react';
import styles from './ButtonGroup.module.scss';
import type { ReactNode, Ref } from 'react';

type Props = {
  /**
   * If `true` the button group will use the entire width
   * @default false
   */
  fullWidth?: boolean;
  /**
   * If `true` there will be no space between the buttons. Rounded borders are removed.
   * @default false
   */
  noSpace?: boolean;
  /**
   * If `responsive`, the button group will use a vertical orientation on small screens and horizontal on large screens
   * @default 'horizontal'
   */
  orientation?: 'horizontal' | 'responsive' | 'vertical';
  /**
   * If `true` there will be a shadow added to the button group
   * @default true
   */
  shadow?: boolean;
  /**
   * A reference for the button group
   */
  ref?: Ref<HTMLDivElement>;
  /**
   * Classname(s) applied to the button group
   * @default ''
   */
  className?: string;
  /**
   * The content of the button group
   */
  children: ReactNode;
};

const ButtonGroup = forwardRef<HTMLDivElement, Props>(
  (
    {
      fullWidth,
      noSpace,
      orientation = 'horizontal',
      shadow = true,
      className,
      children,
    },
    ref,
  ): JSX.Element => {
    const config = {
      orientation: {
        horizontal: `btn-group-horizontal`,
        responsive: `btn-group-responsive`,
        vertical: `btn-group-vertical`,
      },
      spacing: {
        no: {
          horizontal: `btn-group-horizontal-no-spacing`,
          responsive: `btn-group-responsive-no-spacing`,
          vertical: `btn-group-vertical-no-spacing`,
        },
        yes: {
          horizontal: `btn-group-horizontal-spacing`,
          responsive: `btn-group-responsive-spacing`,
          vertical: `btn-group-vertical-spacing`,
        },
      },
    };

    return (
      <div
        ref={ref}
        className={clsx(
          styles['btn-group'],
          styles[config.orientation[orientation]],
          noSpace
            ? [
                styles['btn-group-no-spacing'],
                styles[config.spacing.no[orientation]],
              ]
            : styles[config.spacing.yes[orientation]],
          shadow && noSpace && styles['btn-group-shadow'],
          fullWidth && styles['btn-group-full'],
          className,
        )}
      >
        {children}
      </div>
    );
  },
);

ButtonGroup.displayName = 'ButtonGroup';
export default ButtonGroup;
